import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useGists } from '../contexts/GistContext'
import { useToast } from '../hooks/useToast'
import { useTheme } from '../hooks/useTheme'
import { formatDate } from '../utils/formatDate'
import LanguageBadge from './LanguageBadge'
import SearchInput from './SearchInput'
import { ArrowLeft, Copy, FileText } from '@phosphor-icons/react'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import remarkGfm from 'remark-gfm'
import { common, createLowlight } from 'lowlight'
import dockerfile from 'highlight.js/lib/languages/dockerfile'
import { toHtml } from 'hast-util-to-html'

function MarkdownComponent({ content, fileKey }) {
  return (
    <ReactMarkdown
      key={fileKey}
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHighlight]}
    >
      {content}
    </ReactMarkdown>
  )
}

export default function GistDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { gists } = useGists()
  const toast = useToast()
  const { theme } = useTheme()
  const [activeFileIndex, setActiveFileIndex] = useState(0)
  const scrollContainerRef = useRef(null)
  const lowlight = useRef(() => {
    const ll = createLowlight(common)
    ll.register({ dockerfile })
    return ll
  }).current()

  useEffect(() => {
    const darkLink = document.getElementById('hljs-theme-dark')
    const lightLink = document.getElementById('hljs-theme-light')

    if (darkLink && lightLink) {
      if (theme === 'dracula') {
        darkLink.disabled = false
        lightLink.disabled = true
      } else {
        darkLink.disabled = true
        lightLink.disabled = false
      }
    }
  }, [theme])

  useEffect(() => {
    // Scroll window to top (for mobile view)
    window.scrollTo(0, 0)

    // Scroll internal container to top
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }

    setActiveFileIndex(0)
  }, [id])

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [activeFileIndex])

  if (!id) {
    return (
      <div className="hero min-h-screen bg-base-100">
        <div className="hero-content text-center">
          <div className="max-w-md">
            <FileText size={80} className="mx-auto mb-6 text-primary opacity-50" />
            <h2 className="text-3xl font-bold mb-4">Select a Gist</h2>
            <p className="text-base-content/70">
              Choose a gist from the list to view its contents
            </p>
          </div>
        </div>
      </div>
    )
  }

  const gist = gists.find((g) => g.gist_id === id)

  if (!gist || !gist.files || gist.files.length === 0) {
    return (
      <div className="hero min-h-[50vh]">
        <div className="hero-content text-center">
          <div className="max-w-md">
            <h2 className="text-2xl font-bold">Gist not found</h2>
            <p className="py-4">The requested gist could not be found</p>
            <button onClick={() => navigate('/')} className="btn btn-primary">
              Back to list
            </button>
          </div>
        </div>
      </div>
    )
  }

  const activeFile = gist.files[activeFileIndex] || gist.files[0]

  const isMarkdownFile = (file) => {
    return file.language === 'Markdown' ||
           file.filename.toLowerCase().endsWith('.md') ||
           file.filename.toLowerCase().endsWith('.markdown')
  }

  const getLanguageName = (language) => {
    if (!language) return 'plaintext'
    const langMap = {
      JavaScript: 'javascript',
      TypeScript: 'typescript',
      Python: 'python',
      Ruby: 'ruby',
      Go: 'go',
      Rust: 'rust',
      Java: 'java',
      'C++': 'cpp',
      C: 'c',
      PHP: 'php',
      Shell: 'bash',
      Bash: 'bash',
      Fish: 'fish',
      JSON: 'json',
      YAML: 'yaml',
      Markdown: 'markdown',
      SQL: 'sql',
      CSS: 'css',
      HTML: 'html',
      Lua: 'lua',
      'C#': 'csharp',
      Kotlin: 'kotlin',
      Swift: 'swift',
      Dart: 'dart',
      Elixir: 'elixir',
      Erlang: 'erlang',
      Haskell: 'haskell',
      Scala: 'scala',
      Clojure: 'clojure',
      Docker: 'dockerfile',
      Dockerfile: 'dockerfile',
      GraphQL: 'graphql',
      TOML: 'toml',
      Vim: 'vim',
      XML: 'xml',
    }
    return langMap[language] || 'plaintext'
  }

  const highlightCode = (code, language) => {
    try {
      const langName = getLanguageName(language)
      if (lowlight.registered(langName)) {
        const tree = lowlight.highlight(langName, code, { prefix: 'hljs-' })
        return toHtml(tree)
      } else {
        const tree = lowlight.highlight('plaintext', code, { prefix: 'hljs-' })
        return toHtml(tree)
      }
    } catch (error) {
      console.error('Syntax highlighting error:', error)
      return code
    }
  }

  const copyToClipboard = async (content) => {
    try {
      await navigator.clipboard.writeText(content)
      toast.success('Copied to clipboard')
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }

  return (
    <div className="h-full flex flex-col">
      <div className="lg:hidden p-3 border-b border-base-300 space-y-3">
        <SearchInput />
        <button onClick={() => navigate('/')} className="btn btn-ghost btn-sm">
          <ArrowLeft size={20} />
          Back
        </button>
      </div>

      <div className="p-4 lg:p-6 border-b border-base-300">
        <div className="flex items-start justify-between mb-2">
          <h1 className="text-xl lg:text-2xl font-bold">
            {gist.description || 'Untitled gist'}
          </h1>
          <a
            href={`https://gist.github.com/${gist.gist_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary btn-outline btn-sm"
          >
            Open on GitHub
          </a>
        </div>
        <div className="text-sm text-base-content/70 space-x-4">
          <span>Created {formatDate(gist.gist_created_at)}</span>
          {gist.gist_updated_at !== gist.gist_created_at && (
            <span>Updated {formatDate(gist.gist_updated_at)}</span>
          )}
        </div>
      </div>

      <div className="border-b border-base-300 px-3 lg:px-6">
        <div className="flex gap-2 overflow-x-auto">
          {gist.files.map((file, index) => (
            <button
              key={index}
              className={`px-3 lg:px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                index === activeFileIndex
                  ? 'border-primary text-primary'
                  : 'border-transparent text-base-content/70 hover:text-base-content'
              }`}
              onClick={() => setActiveFileIndex(index)}
            >
              {file.filename}
            </button>
          ))}
        </div>
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-3 lg:p-6">
        <div className="bg-base-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 lg:px-4 py-2 lg:py-3 bg-base-300">
            <div className="flex items-center gap-2">
              {activeFile.language && (
                <LanguageBadge language={activeFile.language} />
              )}
              <span className="text-xs text-base-content/70">
                {activeFile.size} bytes
              </span>
            </div>
            <button
              onClick={() => copyToClipboard(activeFile.content)}
              className="btn btn-ghost btn-xs"
              title="Copy to clipboard"
            >
              <Copy size={16} />
            </button>
          </div>

          <div className="overflow-x-auto">
            {isMarkdownFile(activeFile) ? (
              <div className="prose prose-sm lg:prose w-full !max-w-none !p-3 lg:!p-4 !bg-base-200 dark:prose-invert">
                <MarkdownComponent
                  content={activeFile.content}
                  fileKey={`${gist.gist_id}-${activeFileIndex}`}
                />
              </div>
            ) : (
              <pre className="!m-0 !p-3 lg:!p-4 !bg-base-200 whitespace-pre-wrap break-words text-xs lg:text-sm">
                <code
                  className={`hljs language-${getLanguageName(activeFile.language)}`}
                  dangerouslySetInnerHTML={{
                    __html: highlightCode(activeFile.content, activeFile.language),
                  }}
                />
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
